import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { aiDashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { LATEST } from "../../../../../utils/constants";
import { NHITypeIconsAndLabels } from "../../../../stack/environment/utils";
import {
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  measureContentForPdf,
} from "../../../custom_dashboard/utils/commonWidgetUtils";
import DonutPieChartPercentage from "../../../custom_dashboard/widgets/CriticalAssetPieWidget/DonutPieChartPercentage";
import WidgetWrapper from "../../../custom_dashboard/WidgetWrapper";

const NHITypesDistribution = forwardRef(
  ({ registerWidgetExport, widgetId }, ref) => {
    const colors = useColors();
    const widgetRef = useRef(null);
    const { getNHITypesDistributionTrend } = useAPI();

    const {
      data: nhiTypesDistributionTrend,
      isLoading: isLoadingNHITypesDistribution,
    } = useQuery({
      queryKey: aiDashboardKeys.widgetType("nhiTypesDistribution"),
      queryFn: getNHITypesDistributionTrend,
      select: (data) => data.data,
    });

    const currentTimePoint = useMemo(() => {
      if (
        !nhiTypesDistributionTrend ||
        Object.keys(nhiTypesDistributionTrend).length === 0
      )
        return null;
      return nhiTypesDistributionTrend[LATEST];
    }, [nhiTypesDistributionTrend]);

    const chartDataToDisplay = useMemo(() => {
      if (!currentTimePoint) return [];

      const colorPalette = [
        colors.primary_400,
        colors.primary_300,
        colors.primary_200,
        colors.primary_100,
        colors.primary_50,
        colors.primary_25,
      ];

      const entries = Object.entries(currentTimePoint);
      const top5Entries = entries.slice(0, 5);
      const otherEntries = entries.slice(5);

      const data = top5Entries.map(([key, value], index) => {
        const displayLabel = NHITypeIconsAndLabels[key]?.[1] || key;
        return {
          id: key,
          value: value,
          label: displayLabel,
          color: colorPalette[index % colorPalette.length],
        };
      });

      if (otherEntries.length > 0) {
        const othersCount = otherEntries.reduce(
          (sum, [, count]) => sum + count,
          0
        );
        const othersNames = otherEntries.map(
          ([name]) => NHITypeIconsAndLabels[name]?.[1] || name
        );

        data.push({
          id: "others",
          value: othersCount,
          label: "Others",
          color: colorPalette[5 % colorPalette.length],
          othersNames,
        });
      }

      return data;
    }, [currentTimePoint, colors]);

    const CustomTooltip = ({ othersNames, color }) => {
      if (othersNames?.length > 0) {
        return (
          <Box
            sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: color,
                    width: "8px",
                    height: "8px",
                    borderRadius: "2px",
                  }}
                />
                <Typography variant="overline" sx={{ display: "block" }}>
                  Others
                </Typography>
              </Box>
              <Box sx={{ ml: 1.5 }}>
                {othersNames.map((name, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={{ display: "block" }}
                  >
                    {name === "null" ? "nhi" : name}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        );
      }
    };

    const exportToPdf = async (isCombinedExport = false) => {
      if (!widgetRef.current) return null;

      try {
        const clonedElement = widgetRef.current.cloneNode(true);
        clonedElement.style.width = "800px";
        clonedElement.style.maxWidth = "800px";
        clonedElement.style.backgroundColor = "#ffffff";
        clonedElement.style.padding = "24px";
        clonedElement.style.borderRadius = "12px";
        clonedElement.style.boxShadow = "0px 2px 8px rgba(0, 0, 0, 0.1)";

        if (isCombinedExport) {
          return clonedElement;
        }

        const fullElement = await createPdfContainer();
        const header = createPdfHeader(
          "NHI Types Distribution",
          "AI Posture Dashboard"
        );
        fullElement.appendChild(header);
        fullElement.appendChild(clonedElement);

        const { contentWidth, contentHeight } = await measureContentForPdf(
          fullElement
        );

        const pdfConfig = createPdfConfig(
          "NHI_Types_Distribution_Widget.pdf",
          contentWidth,
          contentHeight
        );

        await html2pdf().set(pdfConfig).from(fullElement).save();

        return null;
      } catch (error) {
        console.error("Error exporting to PDF:", error);
        return null;
      }
    };

    useImperativeHandle(ref, () => ({
      exportToPdf,
      hasExportCapability: true,
    }));

    useEffect(() => {
      if (registerWidgetExport && widgetId) {
        registerWidgetExport(widgetId, exportToPdf);
      }
    }, [registerWidgetExport, widgetId]);

    return (
      <WidgetWrapper
        title="NHI Types Distribution"
        info="NHI count by type across your environment."
      >
        <Box
          ref={widgetRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          {isLoadingNHITypesDistribution ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", height: "100%" }}
            >
              <LoadingIcon size={10} />
            </Box>
          ) : !currentTimePoint || chartDataToDisplay.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <Typography variant="caption">No data available</Typography>
            </Box>
          ) : (
            <Box sx={{ padding: "60px 40px 60px 40px" }}>
              <DonutPieChartPercentage
                data={chartDataToDisplay}
                label="NHIs"
                centerDisplay="total"
                equalSlices={false}
                showLegend={true}
                CustomTooltipComponent={CustomTooltip}
                showTrend={false}
              />
            </Box>
          )}
        </Box>
      </WidgetWrapper>
    );
  }
);

NHITypesDistribution.displayName = "NHITypesDistribution";

export default NHITypesDistribution;
